// @story: none
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@zag/auth/ui';
import { ConfirmDialogService, FloatingPanelComponent, ZagIcon, type FloatingPanelPosition } from '@zag/ui';
import { TestFeedbackService, type RecordedResult } from './test-feedback.service';

/** Editor state while the tester writes a failure comment for one case. */
interface FailEditor {
  caseId: string;
  comment: string;
}

/**
 * Floating test-feedback sidebar for issue verification instances.
 *
 * Renders the issue's Test Script as an interactive checklist inside the
 * running app: cases can be passed with one click or failed with a mandatory
 * comment; "Finish review" posts the protocol as a GitHub issue comment via
 * the app API. Mounted by the layout exclusively on issue hosts.
 */
@Component({
  selector: 'sc-test-feedback-sidebar',
  imports: [FloatingPanelComponent, TranslatePipe, ZagIcon],
  templateUrl: './test-feedback-sidebar.component.html',
  styleUrl: './test-feedback-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestFeedbackSidebarComponent {
  readonly feedback = inject(TestFeedbackService);
  private readonly authService = inject(AuthService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly translate = inject(TranslateService);

  readonly script = computed(() => this.feedback.scriptResource.value()?.script ?? null);
  readonly isLoading = computed(() => this.feedback.scriptResource.status() === 'loading');
  readonly loadFailed = computed(() => this.feedback.scriptResource.status() === 'error');

  /** Starts collapsed when there is nothing to review (AC 3), else restores the persisted state. */
  readonly collapsed = linkedSignal(() => this.loadFailed() || this.feedback.panelCollapsed());
  readonly position = linkedSignal<FloatingPanelPosition | null>(() => this.feedback.panelPosition());

  readonly expandedCases = signal<ReadonlySet<string>>(new Set());
  readonly failEditor = signal<FailEditor | null>(null);
  readonly failEditorMissingComment = signal(false);

  /** Tester name for the protocol — defaults to the logged-in user (AC 14). */
  readonly testerName = linkedSignal(
    () => this.feedback.tester() ?? this.authService.currentUser()?.name ?? this.authService.currentUser()?.email ?? ''
  );

  readonly progressLabel = computed(() => `${this.feedback.resolvedCount()}/${this.feedback.totalCount()}`);
  readonly canFinish = computed(() => this.feedback.resolvedCount() > 0 && this.feedback.submitState().kind !== 'submitting');

  /** Translation key for the current submit error, or `null` when there is none. */
  readonly submitErrorKey = computed(() => {
    const state = this.feedback.submitState();
    if (state.kind !== 'error') return null;
    return state.status === 429 ? 'verification.sidebar.submitRateLimited' : 'verification.sidebar.submitError';
  });

  resultFor(caseId: string): RecordedResult | null {
    return this.feedback.results()[caseId] ?? null;
  }

  toggleCase(caseId: string): void {
    this.expandedCases.update(expanded => {
      const next = new Set(expanded);
      if (next.has(caseId)) {
        next.delete(caseId);
      } else {
        next.add(caseId);
      }
      return next;
    });
  }

  markPassed(caseId: string): void {
    if (this.resultFor(caseId)?.status === 'passed') {
      this.feedback.clearResult(caseId);
      return;
    }
    this.failEditor.set(null);
    this.feedback.recordResult(caseId, { status: 'passed', comment: '' });
  }

  openFailEditor(caseId: string): void {
    this.failEditorMissingComment.set(false);
    this.failEditor.set({ caseId, comment: this.resultFor(caseId)?.comment ?? '' });
  }

  updateFailComment(comment: string): void {
    this.failEditor.update(editor => (editor ? { ...editor, comment } : editor));
    this.failEditorMissingComment.set(false);
  }

  /** Failing a case requires a non-empty comment (AC 8). */
  saveFailResult(): void {
    const editor = this.failEditor();
    if (!editor) return;
    if (!editor.comment.trim()) {
      this.failEditorMissingComment.set(true);
      return;
    }
    this.feedback.recordResult(editor.caseId, { status: 'failed', comment: editor.comment.trim() });
    this.failEditor.set(null);
  }

  cancelFailEditor(): void {
    this.failEditor.set(null);
    this.failEditorMissingComment.set(false);
  }

  updateTesterName(name: string): void {
    this.testerName.set(name);
  }

  onCollapsedChange(collapsed: boolean): void {
    this.collapsed.set(collapsed);
    this.feedback.savePanelState({ position: this.position(), collapsed });
  }

  onPositionChange(position: FloatingPanelPosition | null): void {
    this.position.set(position);
    this.feedback.savePanelState({ position, collapsed: this.collapsed() });
  }

  /** Unresolved cases require an explicit confirmation before posting (AC 11). */
  async finishReview(): Promise<void> {
    const tester = this.testerName().trim();
    if (!tester || !this.canFinish()) return;

    const unresolved = this.feedback.unresolvedCount();
    if (unresolved > 0) {
      const confirmed = await this.confirmDialog.confirm({
        title: this.translate.instant('verification.sidebar.finishConfirmTitle'),
        message: this.translate.instant('verification.sidebar.finishConfirmMessage', { count: unresolved }),
        confirmText: 'verification.sidebar.finishConfirmAction',
      });
      if (!confirmed) return;
    }

    this.feedback.submitProtocol(tester);
  }

  retrySubmit(): void {
    this.feedback.submitProtocol(this.testerName().trim());
  }
}
